import { useRef, useState } from "react";

const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

// Tiny zero-dependency SVG sparkline with optional hover tooltip + vertical guide.
// Props:
//   data: array of numbers (chronological, oldest first)  OR
//   series: array of {date, amount} (preferred — enables tooltip dates)
//   width / height: pixels
//   stroke / fill / strokeWidth: visuals
//   showArea / showDot
//   tooltip: boolean — enable hover guide + tooltip (default false)
//   valueFormatter: (n) => string (default: $ no decimals)
//   testId
export function Sparkline({
  data,
  series,
  width = 110,
  height = 28,
  stroke = "#284B3B",
  fill = "rgba(40,75,59,0.10)",
  strokeWidth = 1.4,
  showArea = true,
  showDot = true,
  tooltip = false,
  valueFormatter = fmtCurrency,
  testId,
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const wrapRef = useRef(null);

  // Normalise to {date, amount}[]
  const points = (() => {
    if (Array.isArray(series) && series.length) return series;
    if (Array.isArray(data) && data.length) return data.map((v) => ({ date: "", amount: v }));
    return [];
  })();

  if (!points.length) {
    return (
      <svg width={width} height={height} data-testid={testId} aria-hidden="true">
        <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="#EAEAE5" strokeWidth={1} />
      </svg>
    );
  }

  const values = points.map((p) => p.amount);
  const n = points.length;
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 1);
  const span = max - min || 1;
  const pad = 2;

  const coords = values.map((v, i) => {
    const x = (i / Math.max(n - 1, 1)) * (width - pad * 2) + pad;
    const y = height - pad - ((v - min) / span) * (height - pad * 2);
    return [x, y];
  });

  const linePath = coords
    .map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`))
    .join(" ");

  const areaPath =
    `${linePath} L${coords[coords.length - 1][0].toFixed(1)},${height - pad} ` +
    `L${coords[0][0].toFixed(1)},${height - pad} Z`;

  const [lastX, lastY] = coords[coords.length - 1];

  const indexAt = (clientX) => {
    if (!wrapRef.current) return null;
    const rect = wrapRef.current.getBoundingClientRect();
    const localX = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const ratio = (localX - pad) / Math.max(width - pad * 2, 1);
    const idx = Math.round(ratio * (n - 1));
    return Math.max(0, Math.min(n - 1, idx));
  };

  const onMove = (e) => {
    if (!tooltip) return;
    setHoverIdx(indexAt(e.clientX));
  };

  const showTip = tooltip && hoverIdx !== null;
  const tipX = showTip ? coords[hoverIdx][0] : 0;
  const tipY = showTip ? coords[hoverIdx][1] : 0;
  const tipDatum = showTip ? points[hoverIdx] : null;

  // Position tooltip box so it doesn't get clipped at edges
  const tipBoxX = showTip ? Math.max(0, Math.min(width - 80, tipX - 40)) : 0;

  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      style={{ width, height, lineHeight: 0 }}
      onMouseMove={onMove}
      onMouseLeave={() => setHoverIdx(null)}
      data-testid={testId}
    >
      <svg width={width} height={height} role="img" aria-label="30-day revenue sparkline">
        {showArea && <path d={areaPath} fill={fill} stroke="none" />}
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {showDot && !showTip && <circle cx={lastX} cy={lastY} r={2} fill={stroke} />}
        {showTip && (
          <>
            <line x1={tipX} y1={pad} x2={tipX} y2={height - pad} stroke={stroke} strokeWidth={0.8} strokeDasharray="2 2" opacity={0.5} />
            <circle cx={tipX} cy={tipY} r={2.6} fill="#fff" stroke={stroke} strokeWidth={1.4} />
          </>
        )}
      </svg>
      {showTip && tipDatum && (
        <div
          className="absolute z-20 pointer-events-none shadow-md rounded-md bg-[#1A211D] text-white px-2 py-1 text-[10px] leading-tight whitespace-nowrap"
          style={{
            left: tipBoxX,
            top: -34,
            minWidth: 60,
            textAlign: "center",
          }}
          data-testid={testId ? `${testId}-tip` : undefined}
        >
          <div className="font-semibold tabular-nums">{valueFormatter(tipDatum.amount)}</div>
          {tipDatum.date && <div className="text-[#A8B0AB] tabular-nums">{fmtDate(tipDatum.date)}</div>}
        </div>
      )}
    </div>
  );
}
